import React, { useEffect, useState, useMemo } from 'react';
import { ClipboardList, Search, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { apiRequest } from '../services/api';
import { format } from 'date-fns';

const fetchSurveys = () => apiRequest('/survey');

export default function SurveyResponses() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchSurveys();
      setSurveys(res.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load survey responses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return surveys;
    return surveys.filter(s =>
      (s.contact_name || '').toLowerCase().includes(q) ||
      (s.contact_phone || '').includes(q) ||
      (s.ground_name || '').toLowerCase().includes(q) ||
      (s.city || '').toLowerCase().includes(q)
    );
  }, [surveys, search]);

  const toggle = (id) => setExpandedId(prev => prev === id ? null : id);

  const PayloadRow = ({ label, value }) => (
    value != null && value !== '' ? (
      <div className="flex gap-2 text-sm">
        <span className="text-ga-text-muted min-w-[160px] shrink-0">{label}</span>
        <span className="text-ga-text-primary font-medium">
          {Array.isArray(value) ? value.join(', ') : String(value)}
        </span>
      </div>
    ) : null
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <ClipboardList className="h-7 w-7 text-ga-primary" />
              Survey Responses
            </h1>
            <p className="text-ga-text-muted mt-1 text-sm">
              Early access submissions from the landing page ({surveys.length} total)
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-ga-border-subtle text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, phone, ground or city…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 w-full rounded-lg border border-ga-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-ga-primary/30"
          />
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="py-16 text-center text-ga-text-muted">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-ga-text-muted">
            {surveys.length === 0 ? 'No survey responses yet.' : 'No results match your search.'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(s => {
              const p = s.payload || {};
              const isOpen = expandedId === s.id;
              return (
                <div key={s.id} className="ga-card rounded-xl border border-ga-border-subtle shadow-sm overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggle(s.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50/50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                      <span className="font-semibold text-ga-text-primary">{s.contact_name}</span>
                      <span className="text-sm text-ga-text-muted">{s.contact_phone}</span>
                      <span className="text-sm text-ga-text-primary">{s.ground_name}</span>
                      <span className="text-sm text-ga-text-muted">{s.city}</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-xs text-ga-text-muted hidden sm:block">
                        {s.submitted_at ? format(new Date(s.submitted_at), 'dd MMM yyyy, HH:mm') : '—'}
                      </span>
                      {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-ga-border-subtle px-4 py-4 space-y-2 bg-gray-50/40">
                      <PayloadRow label="Grounds" value={p.numberOfGrounds} />
                      <PayloadRow label="Monthly bookings" value={p.monthlyBookings} />
                      <PayloadRow label="Booking problems" value={p.bookingProblems} />
                      <PayloadRow label="Staff count" value={p.staffCount} />
                      <PayloadRow label="Staff roles" value={p.roles} />
                      <PayloadRow label="Biggest staff issue" value={p.biggestStaffIssue} />
                      <PayloadRow label="Offline booking issue" value={p.offlineBookingIssue} />
                      <PayloadRow label="Task assignment method" value={p.taskAssignmentMethod} />
                      <PayloadRow label="Payments tracking" value={p.paymentsTrackingMethod} />
                      <PayloadRow label="Revenue mismatch" value={p.revenueMismatch} />
                      <PayloadRow label="Analytics interest" value={p.analyticsInterest ? `${p.analyticsInterest}/5` : null} />
                      <PayloadRow label="Instant fix problem" value={p.instantFixProblem} />
                      <PayloadRow label="Join beta" value={p.joinBeta} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
